import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Grid,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CoffeeOutlined,
  FieldTimeOutlined,
  ReloadOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/es";
import io from "socket.io-client";
import { API_BASE, SOCKET_URL } from "../../config/api";
import { getAuthHeaders } from "../../utils/auth";

dayjs.locale("es");

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

const BREAK_LABELS = {
  meal: "Comida",
  rest: "Descanso",
  personal: "Trámite personal",
  emergency: "Emergencia",
  work_errand: "Salida laboral",
  other: "Otro",
};

const END_LABELS = {
  normal: "Fin normal",
  early: "Salida anticipada",
  emergency: "Emergencia",
  illness: "Enfermedad",
  personal: "Motivo personal",
  authorized: "Salida autorizada",
  other: "Otro",
};

const statusUi = {
  working: { color: "green", label: "Trabajando" },
  on_break: { color: "orange", label: "En pausa" },
  completed: { color: "default", label: "Finalizada" },
};

const formatTime = (value) => (value ? dayjs(value).format("HH:mm") : "—");
const formatMinutes = (value) => {
  const minutes = Math.max(0, Number(value || 0));
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return hours ? `${minutes} min (${hours} h ${remainder} min)` : `${minutes} min`;
};

const metricCardStyle = {
  height: "100%",
  borderRadius: 16,
  border: "1px solid #e9eef5",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
};

export default function Attendance() {
  const screens = useBreakpoint();
  const [mode, setMode] = useState("single");
  const [singleDate, setSingleDate] = useState(dayjs());
  const [range, setRange] = useState([dayjs().startOf("month"), dayjs()]);
  const [employeeId, setEmployeeId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState({ shifts: [], totals: {} });
  const [loading, setLoading] = useState(true);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (mode === "single") {
      params.set("date", singleDate.format("YYYY-MM-DD"));
    } else {
      params.set("from", range?.[0]?.format("YYYY-MM-DD") || dayjs().format("YYYY-MM-DD"));
      params.set("to", range?.[1]?.format("YYYY-MM-DD") || dayjs().format("YYYY-MM-DD"));
    }
    if (employeeId) params.set("employeeId", employeeId);
    return params.toString();
  }, [employeeId, mode, range, singleDate]);

  const fetchAttendance = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/attendance?${queryString}`, {
        headers: getAuthHeaders(),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.message || "No se pudo cargar la asistencia");
      setData(result);
    } catch (error) {
      message.error(error.message || "No se pudo cargar la asistencia");
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const response = await fetch(`${API_BASE}/employees`, { headers: getAuthHeaders() });
        const result = await response.json();
        if (response.ok) setEmployees(Array.isArray(result) ? result : []);
      } catch {
        setEmployees([]);
      }
    };
    loadEmployees();
  }, []);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  useEffect(() => {
    const socket = io(SOCKET_URL);
    const refresh = () => fetchAttendance(false);
    socket.on("attendance:updated", refresh);
    return () => {
      socket.off("attendance:updated", refresh);
      socket.disconnect();
    };
  }, [fetchAttendance]);

  useEffect(() => {
    const interval = window.setInterval(() => fetchAttendance(false), 60000);
    return () => window.clearInterval(interval);
  }, [fetchAttendance]);

  const employeeOptions = useMemo(
    () => employees
      .map((employee) => ({ value: String(employee.id), label: employee.name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [employees]
  );

  const columns = [
    {
      title: "Empleado",
      key: "employee",
      width: 190,
      fixed: screens.lg ? "left" : undefined,
      render: (_, shift) => (
        <Space>
          <Avatar size="small" style={{ background: shift.employee?.background_color || "#1677ff" }}>
            {(shift.employee?.name || "E").slice(0, 1).toUpperCase()}
          </Avatar>
          <div>
            <Text strong>{shift.employee?.name || "Empleado eliminado"}</Text>
            {shift.employee?.email ? <><br /><Text type="secondary" style={{ fontSize: 11 }}>{shift.employee.email}</Text></> : null}
          </div>
        </Space>
      ),
    },
    {
      title: "Fecha",
      dataIndex: "workDate",
      width: 110,
      render: (value) => <Text strong>{dayjs(value).format("DD/MM/YYYY")}</Text>,
    },
    {
      title: "Entrada",
      dataIndex: "startedAt",
      width: 85,
      align: "center",
      render: formatTime,
    },
    {
      title: "Salida",
      dataIndex: "endedAt",
      width: 85,
      align: "center",
      render: formatTime,
    },
    {
      title: "Estado",
      dataIndex: "status",
      width: 110,
      align: "center",
      render: (value) => {
        const item = statusUi[value] || statusUi.completed;
        return <Tag color={item.color}>{item.label}</Tag>;
      },
    },
    {
      title: "Permanencia",
      dataIndex: "totalMinutes",
      width: 145,
      render: formatMinutes,
    },
    {
      title: "Pausas",
      dataIndex: "breakMinutes",
      width: 135,
      render: formatMinutes,
    },
    {
      title: "Trabajados",
      dataIndex: "workedMinutes",
      width: 155,
      render: (value) => <Text strong style={{ color: "#1677ff" }}>{formatMinutes(value)}</Text>,
    },
    {
      title: "Motivo de salida",
      dataIndex: "endReason",
      width: 180,
      render: (value, shift) => value ? (
        <div><Text>{END_LABELS[value] || value}</Text>{shift.endNote ? <><br /><Text type="secondary" style={{ fontSize: 11 }}>{shift.endNote}</Text></> : null}</div>
      ) : <Text type="secondary">—</Text>,
    },
  ].map((column) => ({
    ...column,
    onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
  }));

  const expandedRowRender = (shift) => {
    if (!shift.breaks?.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin pausas ni salidas durante esta jornada" />;
    return (
      <Table
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={shift.breaks}
        columns={[
          { title: "Motivo", dataIndex: "type", render: (value) => <Text strong>{BREAK_LABELS[value] || value}</Text> },
          { title: "Desde", dataIndex: "startedAt", render: formatTime },
          { title: "Hasta", dataIndex: "endedAt", render: formatTime },
          { title: "Duración", dataIndex: "minutes", render: (value) => `${value} min` },
          { title: "Cómputo", dataIndex: "countsAsWork", render: (value) => value ? <Tag color="blue">Salida laboral</Tag> : <Tag>Descontada</Tag> },
          { title: "Nota", dataIndex: "note", render: (value) => value || "—" },
        ]}
        scroll={{ x: 720 }}
      />
    );
  };

  const totals = data?.totals || {};

  return (
    <div style={{ width: "100%", padding: screens.md ? 12 : 0 }}>
      <Card bordered={false} style={{ borderRadius: screens.md ? 20 : 0, border: screens.md ? "1px solid #e9eef5" : "none", boxShadow: screens.md ? "0 12px 34px rgba(15, 23, 42, 0.05)" : "none" }} styles={{ body: { padding: screens.md ? 28 : 16 } }}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            <Space align="start" size={14}>
              <Avatar size={48} shape="square" icon={<ClockCircleOutlined />} style={{ background: "#e6f4ff", color: "#1677ff", borderRadius: 14 }} />
              <div>
                <Text style={{ color: "#1677ff", fontWeight: 800, fontSize: 11, letterSpacing: 1.1 }}>CONTROL DE ASISTENCIA</Text>
                <Title level={2} style={{ margin: "3px 0 2px" }}>Jornadas y minutos trabajados</Title>
                <Text type="secondary">Revisá entradas, salidas, pausas y tiempo efectivo por empleado.</Text>
              </div>
            </Space>
          </Col>
          <Col xs={24} lg={8} style={{ textAlign: screens.lg ? "right" : "left" }}>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => fetchAttendance()}>Actualizar</Button>
          </Col>
        </Row>

        <Card size="small" style={{ marginTop: 24, borderRadius: 16, background: "#f8fafc", borderColor: "#e7edf5" }}>
          <Row gutter={[14, 14]} align="bottom">
            <Col xs={24} md={8} lg={5}>
              <Text strong style={{ display: "block", marginBottom: 7 }}>Período</Text>
              <Segmented block value={mode} onChange={setMode} options={[{ label: "Una fecha", value: "single" }, { label: "Desde / hasta", value: "range" }]} />
            </Col>
            <Col xs={24} md={8} lg={mode === "single" ? 6 : 9}>
              <Text strong style={{ display: "block", marginBottom: 7 }}>{mode === "single" ? "Fecha" : "Rango de fechas"}</Text>
              {mode === "single" ? (
                <DatePicker allowClear={false} value={singleDate} onChange={(value) => value && setSingleDate(value)} format="DD/MM/YYYY" style={{ width: "100%" }} suffixIcon={<CalendarOutlined />} />
              ) : (
                <RangePicker allowClear={false} value={range} onChange={(value) => value && setRange(value)} format="DD/MM/YYYY" style={{ width: "100%" }} />
              )}
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Text strong style={{ display: "block", marginBottom: 7 }}>Empleado</Text>
              <Select allowClear showSearch optionFilterProp="label" value={employeeId} onChange={setEmployeeId} placeholder="Todos los empleados" options={employeeOptions} style={{ width: "100%" }} />
            </Col>
          </Row>
        </Card>

        <Row gutter={[14, 14]} style={{ marginTop: 20 }}>
          <Col xs={12} lg={6}><Card bordered={false} style={metricCardStyle}><Statistic title="Jornadas" value={totals.shifts || 0} prefix={<TeamOutlined style={{ color: "#64748b" }} />} /></Card></Col>
          <Col xs={12} lg={6}><Card bordered={false} style={metricCardStyle}><Statistic title="Minutos trabajados" value={totals.workedMinutes || 0} suffix="min" prefix={<CheckCircleOutlined style={{ color: "#16a34a" }} />} /></Card></Col>
          <Col xs={12} lg={6}><Card bordered={false} style={metricCardStyle}><Statistic title="Minutos de pausa" value={totals.breakMinutes || 0} suffix="min" prefix={<CoffeeOutlined style={{ color: "#d97706" }} />} /></Card></Col>
          <Col xs={12} lg={6}><Card bordered={false} style={metricCardStyle}><Statistic title="Permanencia total" value={totals.totalMinutes || 0} suffix="min" prefix={<FieldTimeOutlined style={{ color: "#1677ff" }} />} /></Card></Col>
        </Row>

        <div style={{ marginTop: 22 }}>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={data?.shifts || []}
            scroll={{ x: 1295 }}
            pagination={{ pageSize: 12, showSizeChanger: false, showTotal: (total) => `${total} jornadas` }}
            expandable={{ expandedRowRender, rowExpandable: (shift) => Boolean(shift.breaks?.length) }}
            locale={{ emptyText: <Empty description="No hay fichajes para el período seleccionado" /> }}
          />
        </div>
      </Card>
    </div>
  );
}
